import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, message } from "antd";
import { useGlobal } from "../global/user";
import { Companies } from "../api";
import { themes } from "../themes/themes";
import { resolveAssetUrl } from "../context/brandingDefaults";
import CompanyModal from "./modal/CompanyModal";

const themeLabel = (name) => (name && themes[name]?.displayName) || name || "基本";

export default function CompanyTable() {
  const { user } = useGlobal();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [targetCompany, setTargetCompany] = useState(null);

  // 獲取公司數據
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Companies.getAllCompanies();
      setData(response);
    } catch (error) {
      message.error("獲取公司數據失敗");
      console.error("Failed to fetch companies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.class) {
      fetchCompanies();
    }
  }, [user.class, fetchCompanies]);

  // 監聽公司創建事件
  useEffect(() => {
    const handleCompanyCreated = () => {
      console.log('CompanyTable: 檢測到新公司創建，刷新列表');
      fetchCompanies();
    };

    window.addEventListener('companyCreated', handleCompanyCreated);

    return () => {
      window.removeEventListener('companyCreated', handleCompanyCreated);
    };
  }, [fetchCompanies]);

  // 表格列定義
  const columns = [
    {
      title: "公司名稱",
      dataIndex: "company_name",
      key: "company_name",
      minWidth: 150
    },
    {
      title: "Logo",
      dataIndex: "logo_url",
      key: "logo_url",
      align: "center",
      width: 140,
      render: (url) =>
        url ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px 8px",
              borderRadius: 4,
              background: "var(--header-background)",
            }}
          >
            <img
              src={resolveAssetUrl(url)}
              alt=""
              style={{ height: 24, width: "auto", objectFit: "contain" }}
            />
          </div>
        ) : (
          <span style={{ color: "var(--secondary-text)" }}>預設</span>
        ),
    },
    {
      title: "網站標題",
      dataIndex: "site_title",
      key: "site_title",
      render: (title) => title || <span style={{ color: "var(--secondary-text)" }}>預設</span>,
      minWidth: 160
    },
    {
      title: "登入網址代碼",
      dataIndex: "login_slug",
      key: "login_slug",
      render: (slug) =>
        slug ? (
          <a
            href={`${window.location.origin}${process.env.PUBLIC_URL || ""}/#/${slug}`}
            target="_blank"
            rel="noreferrer"
          >
            {slug}
          </a>
        ) : (
          <span style={{ color: "var(--secondary-text)" }}>未設定</span>
        ),
      minWidth: 140
    },
    {
      title: "主題名稱",
      dataIndex: "theme_name",
      key: "theme_name",
      render: (name) => themeLabel(name),
      minWidth: 120
    },
    {
      title: "主題購買時間",
      dataIndex: "theme_purchased_at",
      key: "theme_purchased_at",
      align: "center",
      minWidth: 180
    },
    {
      title: "編輯公司",
      key: "edit",
      render: (_, record) => (
        <Button
          onClick={() => {
            setTargetCompany(record);
            setModalVisible(true);
          }}
        >
          編輯公司
        </Button>
      ),
      align: "center",
      minWidth: 120
    },
  ];

  // 處理模態框關閉
  const handleModalClose = () => {
    setModalVisible(false);
    setTargetCompany(null);
  };

  // 處理公司數據更新
  const handleCompanyUpdate = useCallback((updatedData) => {
    setData(updatedData);
  }, []);

  return (
    <React.Fragment>
      <Button
        className="mb-2"
        style={{
          float: "left",
          marginBottom: 20,
        }}
        onClick={() => {
          setTargetCompany(null);
          setModalVisible(true);
        }}
      >
        新增公司
      </Button>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showTotal: (total) => `共 ${total} 筆資料`,
        }}
        scroll={{ x: "max-content" }}
        bordered
      />

      <CompanyModal
        visible={modalVisible}
        setVisible={setModalVisible}
        setCompanies={handleCompanyUpdate}
        value={targetCompany}
        onClose={handleModalClose}
        onChanged={fetchCompanies}
      />
    </React.Fragment>
  );
}